import React, { useCallback, useState } from "react";
import { TIMELINE_DRAGGABLE_TIME_LINE_WIDTH, TIMELINE_PADDING } from "../core/layoutConstants";
import { Duration } from "luxon";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import * as Slider from "@opstel/radix/Slider";

const DRAGGABLE_ITEM_WIDTH = 58;

// The time slider in the time line header is very slightly offset from the line in the SBD stage.
// This fixes the offset to align the two components.
// The value has been obtained through experimentation.
// It may have to be different for each browser.
const ALIGNMENT_FIX = 0.01;

export interface DraggableTimeSelectorProps {
  readonly width: number;
  readonly onDragStart?: () => void;
  readonly onDragEnd?: () => void;
}

export function DraggableTimeSelector({
  width,
  onDragStart,
  onDragEnd,
}: DraggableTimeSelectorProps): React.JSX.Element {
  const selectedTime = useSelectedTimeStore((state) => state.selectedTime)!;
  const scenarioInterval = useSelectedTimeStore((state) => state.scenarioInterval);
  const setDateTime = useSelectedTimeStore((state) => state.setDateTime);

  const [isPointerDown, setIsPointerDown] = useState(false);

  const onPointerDown = useCallback(() => {
    setIsPointerDown(true);
    if (onDragStart) {
      onDragStart();
    }
  }, [onDragStart]);

  const onPointerUp = useCallback(() => {
    setIsPointerDown(false);
    if (onDragEnd) {
      onDragEnd();
    }
  }, [onDragEnd]);

  const onValueChange = useCallback(
    ([minutes]: [number]) =>
      scenarioInterval &&
      setDateTime(scenarioInterval.start.plus(Duration.fromObject({ minutes }))),
    [setDateTime, scenarioInterval],
  );

  return (
    <div
      className="absolute top-0 left-0 h-full touch-none overflow-hidden"
      style={{ width: `${width}px` }}
    >
      <Slider.Root
        data-testid="sbd-draggable-time-selector-slider"
        className="relative z-50 flex h-full touch-none items-center select-none"
        style={{
          marginLeft: `${TIMELINE_PADDING + ALIGNMENT_FIX}px`,
          marginRight: `${TIMELINE_PADDING - ALIGNMENT_FIX}px`,
        }}
        value={scenarioInterval ? [selectedTime.diff(scenarioInterval.start).as("minutes")] : []}
        max={scenarioInterval ? scenarioInterval.end.diff(scenarioInterval.start).as("minutes") : 0}
        step={1}
        onValueChange={onValueChange}
      >
        <Slider.Track className="relative h-full grow">
          <Slider.Range className="absolute h-full" />
        </Slider.Track>
        <Slider.Thumb
          data-testid="sbd-draggable-time-selector-thumb"
          className={`relative top-[10px] z-10 block h-6 w-0 align-middle leading-6 ${isPointerDown ? "cursor-grabbing" : "cursor-grab"}`}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          aria-label="Selected time"
        >
          <div
            className={`font-weight-medium absolute bottom-0 z-10 block h-6 rounded-m bg-default-inverse text-center align-middle text-xs leading-6! text-content-inverse`}
            style={{
              width: `${DRAGGABLE_ITEM_WIDTH}px`,
              left: `calc(50% - ${DRAGGABLE_ITEM_WIDTH / 2}px)`,
            }}
          >
            {selectedTime.toFormat("HH:mm")}
            <div
              className="absolute h-[10px] bg-default-inverse"
              style={{
                left: `calc(50% - ${TIMELINE_DRAGGABLE_TIME_LINE_WIDTH / 2}px)`,
                width: `${TIMELINE_DRAGGABLE_TIME_LINE_WIDTH}px`,
              }}
            />
          </div>
        </Slider.Thumb>
      </Slider.Root>
    </div>
  );
}
